'use client'
import React, { useEffect, useState } from 'react'
import type { LayoutPaneReactElement } from './Types/LayoutPaneReactElement'

import { getInnerChildren } from './helpers/get-inner-children'
import { getOuterChildren } from './helpers/get-outer-children'
import { OuterLayout } from './Partials/OuterLayout'
import { InnerLayout } from './Partials/InnerLayout'
import { RetainBehaviorModifier, RetainSizeModifier } from '../../atoms/Retain/constants/modifiers'
import { useLayoutContext } from '../../../providers/layout-provider'
import { Retain } from '../../atoms/Retain/Retain'
import { mergeDelegatedPanesIntoLayoutChildren } from './helpers/merge-delegated-panes'
import { convertChildrenToElementsArray } from './helpers/convert-children-to-elements-array'
import { determineSidebarState } from './Panes/helpers/determine-sidebar-state'
import { mergeRetainModifiers } from './helpers/merge-retain-modifiers'
import { LayoutProps } from './Types/layoutTypes'
import { ContentWrapper } from './Partials/ContentWrapper'

/**
 * @deprecated PREFER LayoutRSC OVER THIS COMPONENT!!! (import {LayoutRSC as Layout}... LayoutRSC may be renamed to Layout)
 *
 * @description
 * The Layout takes children, disects them and places them in a dom structure.
 * It also listens to the LayoutProvider Context when it exists, for children that are provided through the LayoutDelegator.
 *
 * The LayoutProvider also stores the state of the panes. For example:
 * 1. The LayoutSidebarPane is closed from the LayoutTopPane
 * 2. The LayoutSidebarPane listening to the provider context then closes
 * 3. The LayoutContentPane listening to the provider context, adjusts it's padding and spacing if necessary
 *
 * @todo make Layout independent of LayoutProvider so it can be used without it when no LayoutDelegator is used
 * @see {@link https://jira.hosted-tools.com/browse/STOPEN-5761}
 */
export const Layout = ({
    children,
    retainSize,
    retainBehaviorModifiers,
    noSpacingOnInnerLayout,
    notMain,
    useProviderContext = true,
}: LayoutProps): React.ReactElement => {
    const childrenAsArray = convertChildrenToElementsArray(children) as LayoutPaneReactElement[]

    /** sepparates children in two groups so it can be passed to the InnerLayout and OuterLayout */
    const [innerChildren, setInnerChildren] = useState(getInnerChildren(childrenAsArray))
    const [outerChildren, setOuterChildren] = useState(getOuterChildren(childrenAsArray))

    if (!!useProviderContext) {
        /** If the children or the state of the layout provider change, update the layout of the children */
        const layoutContext = useLayoutContext()
        console.assert(!!layoutContext, 'LayoutContext is not available')

        const { layoutState, cleanUpContext } = layoutContext
        useEffect(() => {
            const mergedChildren = mergeDelegatedPanesIntoLayoutChildren(
                convertChildrenToElementsArray(children ?? []) as LayoutPaneReactElement[],
                convertChildrenToElementsArray(layoutState.layoutPanes ?? []) as LayoutPaneReactElement[],
            )
            setInnerChildren(getInnerChildren(mergedChildren))
            setOuterChildren(getOuterChildren(mergedChildren))
            cleanUpContext(mergedChildren)
        }, [children, layoutState])
    }

    return (
        <Retain
            behaviorModifiers={mergeRetainModifiers([RetainBehaviorModifier.NO_PADDING], retainBehaviorModifiers)}
            sizeModifier={RetainSizeModifier.FULL_WIDTH}
        >
            <ContentWrapper notMain={notMain}>
                <OuterLayout outerChildren={outerChildren}>
                    <InnerLayout
                        retainSize={retainSize}
                        innerChildren={innerChildren}
                        noPadding={noSpacingOnInnerLayout}
                        {...determineSidebarState(childrenAsArray)}
                        key="inner-layout"
                    />
                </OuterLayout>
            </ContentWrapper>
        </Retain>
    )
}
